import React from "react";
import { useTranslation } from "react-i18next";

import { DiffDecision, RiskDiffSection } from "./types";

// Field labels reuse the existing form.risk.* / form.mitigations.* keys
// (plan §7); rendering order follows these maps.
const LABEL_KEYS: Record<string, Record<string, string>> = {
  basics: {
    readable_name: "form.risk.readable_name",
    description: "form.risk.description",
    protection_goal: "form.risk.protection_goal",
    personal: "form.risk.personal",
  },
  classification: {
    group_id: "form.risk.group",
    origin_id: "form.risk.origin",
    consequence_id: "form.risk.consequence",
    phase_id: "form.risk.phase",
  },
  assessment: {
    impact: "form.risk.impact",
    people_count: "form.risk.people_count",
    duration: "form.risk.duration",
    detectability: "form.risk.detectability",
    probability: "form.risk.probability",
    score: "risks.ai.review.summary.score",
  },
  reasoning: {
    reasoning: "risks.ai.detail.reasoning.headline",
  },
  mitigation: {
    type: "form.mitigations.type",
    description: "form.mitigations.description",
    pictogram: "form.mitigations.pictogram",
    impact: "form.mitigations.impact",
    people_count: "form.mitigations.people_count",
    duration: "form.mitigations.duration",
    detectability: "form.mitigations.detectability",
    probability: "form.mitigations.probability",
    left_risk: "form.mitigations.left_risk",
    applied_standards: "form.mitigations.applied_standards",
    reasoning: "risks.ai.detail.mitigationReasoning.headline",
  },
};

const labelGroupForKind = (kind: RiskDiffSection["kind"]): string =>
  kind.startsWith("mitigation") ? "mitigation" : kind;

const renderValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "—";
  }

  return String(value);
};

interface PayloadListProps {
  kind: RiskDiffSection["kind"];
  payload: unknown | null;
}

const PayloadList = ({ kind, payload }: PayloadListProps) => {
  const { t } = useTranslation();

  if (!payload) {
    return <p className="text-sm text-gray-400">—</p>;
  }

  const values = payload as Record<string, unknown>;
  const labels = LABEL_KEYS[labelGroupForKind(kind)];

  return (
    <dl className="space-y-1">
      {Object.entries(labels)
        .filter(([field]) => field in values)
        .map(([field, labelKey]) => (
          <div key={field} className="text-sm">
            <dt className="inline font-medium text-gray-500">
              {t(labelKey)}
              {": "}
            </dt>
            <dd className="inline text-gray-800 whitespace-pre-line">
              {renderValue(values[field])}
            </dd>
          </div>
        ))}
    </dl>
  );
};

interface DiffSectionCardProps {
  section: RiskDiffSection;
  decision?: DiffDecision;
  onDecide: (decision: DiffDecision) => void;
}

// One diff section (plan §7): changed sections render Current vs Proposed
// side by side with explicit [Keep current] / [Take proposal] toggles;
// unchanged sections stay listed but collapsed, auto-marked identical —
// visibility without decision burden. No accept-all/reject-all exists.
export const DiffSectionCard = ({
  section,
  decision,
  onDecide,
}: DiffSectionCardProps) => {
  const { t } = useTranslation();

  const title = section.kind.startsWith("mitigation")
    ? t("risks.ai.diff.section.mitigation", {
        index: (section.mitigationIndex ?? 0) + 1,
      })
    : t(`risks.ai.diff.section.${section.kind}`);

  if (!section.changed) {
    return (
      <details
        data-test={`ai-diff-${section.key}`}
        className="border border-gray-200 rounded-lg bg-white"
      >
        <summary className="px-4 py-2 text-sm text-gray-500 cursor-pointer hover:text-gray-700">
          <span className="font-medium">{title}</span>
          <span className="ml-2">{t("risks.ai.diff.identical")}</span>
        </summary>
        <div className="px-4 pb-3">
          <PayloadList kind={section.kind} payload={section.current} />
        </div>
      </details>
    );
  }

  const toggleClasses = (active: boolean) =>
    `px-3 py-1.5 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-cyan-500 ${
      active
        ? "bg-cyan-600 border-cyan-600 text-white"
        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
    }`;

  return (
    <div
      data-test={`ai-diff-${section.key}`}
      className={`border rounded-lg bg-white p-4 ${
        decision ? "border-cyan-400" : "border-yellow-400"
      }`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        {section.kind === "mitigation_added" && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {t("risks.ai.diff.newMitigation")}
          </span>
        )}
        {section.kind === "mitigation_removed" && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {t("risks.ai.diff.removedMitigation")}
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
            {t("risks.ai.diff.current")}
          </p>
          <PayloadList kind={section.kind} payload={section.current} />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
            {t("risks.ai.diff.proposed")}
          </p>
          <PayloadList kind={section.kind} payload={section.proposed} />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          aria-pressed={decision === "keep"}
          onClick={() => onDecide("keep")}
          className={toggleClasses(decision === "keep")}
        >
          {t("risks.ai.diff.keepCurrent")}
        </button>
        <button
          type="button"
          aria-pressed={decision === "take"}
          onClick={() => onDecide("take")}
          className={toggleClasses(decision === "take")}
        >
          {t("risks.ai.diff.takeProposal")}
        </button>
      </div>
    </div>
  );
};
